import {
  call,
  fork,
  put,
  take,
  takeEvery,
  getContext,
  select,
  ActionPattern,
} from 'redux-saga/effects';
import { ApolloQueryResult } from 'apollo-client';
import { eventChannel } from 'redux-saga';
import * as Sentry from '@sentry/browser';
import { Keycloak, TokensExtended } from '@unitedtraders/keycloak';
import { actions, LOGOUT, LOGIN, TOKENS_REFRESH } from '../actions/app';
import closeChannelOnEnd from '../utils/closeChannelOnEnd';
import { SagaContext } from '../types/common';
import { Services } from 'init/services';
import { currentUserQuery } from '../queries/CurrentUser';
import { CurrentUser } from '../queries/CurrentUser/__generated__/CurrentUser';
import * as dataLayer from '../utils/gtmDataLayer';
import { isAuthenticated as isAuthenticatedSelector } from 'shared/selectors/app';

function keycloakChannel(keycloakService: Keycloak) {
  return eventChannel((emit) => {
    const changeTokensHandler = ({
      accessToken,
      refreshToken,
      parsedAccessToken,
    }: TokensExtended) =>
      emit(actions.refreshToken(accessToken, refreshToken, parsedAccessToken.user_id));
    const removeTokensHandler = () => emit(actions.removeToken());
    const updateHandler = (message: string) => emit(actions.touchTokens());

    keycloakService.on('change:tokens', changeTokensHandler);
    keycloakService.on('remove:tokens', removeTokensHandler);
    keycloakService.on('update', updateHandler);
    return () => {
      keycloakService.removeListener('change:tokens', changeTokensHandler);
      keycloakService.removeListener('remove:tokens', removeTokensHandler);
      keycloakService.removeListener('update', updateHandler);
    };
  });
}

/** @deprecated */
function publishUserInfoUpdate(eventLabel: 'id' | 'name' | 'email', eventValue: string) {
  dataLayer.push({
    eventAction: 'updateUserInfo',
    eventLabel,
    eventValue,
  });
}

function pushUserInfoToGtm(
  userId: string | number | null = null,
  userName: string | null = null,
  userEmail: string | null = null
) {
  dataLayer.pushVariables({ event: 'updateUserInfo', userId, userName, userEmail });
}

function* updateUserInfo() {
  const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
    SagaContext.ApolloClient
  );

  const { data }: ApolloQueryResult<CurrentUser> = yield call(() =>
    apolloClient.query(currentUserQuery())
  );
  const { me } = data.userDirectory;
  if (me) {
    setTimeout(() => {
      pushUserInfoToGtm(me.id, `${me.firstName} ${me.lastName}`, me.email);

      publishUserInfoUpdate('id', `${me.id}`);
      publishUserInfoUpdate('name', `${me.firstName} ${me.lastName}`);
      publishUserInfoUpdate('email', me.email);
    }, 2000); // dumb hack: wait 2s for gtm tags initializing
  }

  if (me) {
    Sentry.configureScope((scope) => scope.setUser({ id: `${me.id}` }));
  }
}

function resetUserInfo() {
  pushUserInfoToGtm();
  Sentry.configureScope((scope) => scope.setUser(null));
}

export default function* tokensSaga() {
  const keycloakService: Services[SagaContext.KeycloakService] = yield getContext(
    SagaContext.KeycloakService
  );
  const apolloClient: Services[SagaContext.ApolloClient] = yield getContext(
    SagaContext.ApolloClient
  );
  const channel: ActionPattern = yield call(keycloakChannel, keycloakService);
  yield fork(closeChannelOnEnd, channel);

  yield takeEvery([LOGOUT], function* () {
    keycloakService.logout();
    yield call(apolloClient.resetStore);
    resetUserInfo();
  });

  yield takeEvery([LOGIN, TOKENS_REFRESH], updateUserInfo);

  const { isAuthenticated }: ReturnType<typeof isAuthenticatedSelector> = yield select(
    isAuthenticatedSelector
  );
  if (isAuthenticated && typeof window !== 'undefined') {
    yield call(updateUserInfo);
  }

  while (true) {
    // @ts-ignore
    yield put(yield take(channel));
  }
}
